import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  checkClockTime,
  checkDietOrder,
  DIET_ORDER_ERROR_MESSAGES,
  eatDayOf,
  formatEatTime,
  formatIsoDay,
  KITCHEN_NOTE_MAX_LENGTH,
} from '@wodi/domain';
import type { DietOrderView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  DateField,
  Dialog,
  DialogActions,
  Notice,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { dietOptionsQuery, kitchenKeys, placeDietOrder } from '../api/kitchen.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { toasts } from '../toasts.ts';

// Order or change an inpatient's diet (docs/plans/phase3-kitchen-and-housekeeping.md §1.1).
// A change writes a new version; the one before is kept. Diet types and textures are the
// facility's lists (TODO(clinical-verify)); Wodi applies no rule to them.

const NO_TEXTURE = 'none';

/** An instant from an East Africa day and `HH:MM`, or null when either is missing or wrong. */
function eatInstant(day: string | null, time: string): Date | null {
  if (day === null) return null;
  const clock = checkClockTime(time);
  if (!clock.ok) return null;
  return new Date(`${day}T${clock.value}:00+03:00`);
}

export function DietDialog({
  admissionId,
  current,
  onClose,
}: {
  admissionId: string;
  current: DietOrderView | null;
  onClose: () => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const options = useQuery(dietOptionsQuery);
  const [now] = useState(() => new Date());
  const today = formatIsoDay(eatDayOf(now));
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [diet, setDiet] = useState<string | null>(current?.diet.code ?? null);
  const [texture, setTexture] = useState<string>(current?.texture?.code ?? NO_TEXTURE);
  const [fluid, setFluid] = useState(
    current?.fluidRestrictionMl == null ? '' : String(current.fluidRestrictionMl),
  );
  const [nbm, setNbm] = useState(current?.nilByMouth ?? false);
  const [fromDay, setFromDay] = useState<string | null>(today);
  const [fromTime, setFromTime] = useState(formatEatTime(now));
  const [untilDay, setUntilDay] = useState<string | null>(null);
  const [untilTime, setUntilTime] = useState('');
  const [note, setNote] = useState(current?.kitchenNote ?? '');
  const [tried, setTried] = useState(false);

  const fluidMl = fluid.trim() === '' ? null : Number(fluid.trim());
  const nbmFrom = nbm ? eatInstant(fromDay, fromTime) : null;
  const nbmUntil = nbm && untilDay !== null ? eatInstant(untilDay, untilTime) : null;
  const untilIncomplete = nbm && untilDay !== null && nbmUntil === null;
  const checked = checkDietOrder(
    {
      fluidRestrictionMl: fluidMl !== null && Number.isNaN(fluidMl) ? -1 : fluidMl,
      nilByMouth: nbm,
      nbmFrom,
      nbmUntil,
      kitchenNote: note,
    },
    new Date(),
  );
  const problem = checked.ok ? null : DIET_ORDER_ERROR_MESSAGES[checked.code];
  const ready = diet !== null && checked.ok && !untilIncomplete;

  const place = useMutation({
    mutationFn: () => {
      if (!checked.ok) throw new Error('not ready');
      return placeDietOrder(admissionId, {
        clientRequestId,
        dietCode: diet ?? '',
        textureCode: texture === NO_TEXTURE ? null : texture,
        fluidRestrictionMl: checked.value.fluidRestrictionMl,
        nilByMouth: checked.value.nilByMouth,
        nbmFrom: checked.value.nbmFrom?.toISOString() ?? null,
        nbmUntil: checked.value.nbmUntil?.toISOString() ?? null,
        kitchenNote: checked.value.kitchenNote,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: kitchenKeys.all });
      toasts.show({ title: current === null ? 'Diet ordered' : 'Diet changed' });
      onClose();
    },
  });

  return (
    <Dialog
      title={current === null ? 'Order diet' : 'Change diet'}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        {options.isError ? (
          <Notice tone="error">The diet lists couldn&apos;t be loaded. Close and try again.</Notice>
        ) : null}
        {current === null ? null : (
          <p className={styles.meta}>
            The current diet is kept in the history; this one replaces it from now.
          </p>
        )}
        <div className={styles.fields}>
          <Select
            label="Diet"
            placeholder={options.isPending ? 'Loading…' : 'Choose a diet'}
            options={(options.data?.diets ?? [])
              .filter((d) => d.active)
              .map((d) => ({ id: d.code, label: d.display }))}
            value={diet}
            onChange={(key) => {
              setDiet(key === null ? null : String(key));
            }}
            {...(tried && diet === null ? { errorMessage: 'Choose a diet.' } : {})}
          />
          <Select
            label="Texture"
            options={[
              { id: NO_TEXTURE, label: 'No texture change' },
              ...(options.data?.textures ?? [])
                .filter((t) => t.active)
                .map((t) => ({ id: t.code, label: t.display })),
            ]}
            value={texture}
            onChange={(key) => {
              setTexture(String(key));
            }}
          />
          <TextField
            label="Fluid restriction (optional)"
            unit="mL a day"
            inputMode="numeric"
            value={fluid}
            onChange={setFluid}
            {...(tried && !checked.ok && checked.code === 'diet.fluid_invalid'
              ? { errorMessage: DIET_ORDER_ERROR_MESSAGES['diet.fluid_invalid'] }
              : {})}
          />
        </div>
        <Checkbox isSelected={nbm} onChange={setNbm} description="No food or drink by mouth.">
          Nil by mouth
        </Checkbox>
        {nbm ? (
          <>
            <div className={styles.fields}>
              <DateField label="Nil by mouth from" value={fromDay} onChange={setFromDay} />
              <TextField
                label="From time"
                description="24-hour clock, like 06:00."
                value={fromTime}
                onChange={setFromTime}
              />
            </div>
            <div className={styles.fields}>
              <DateField
                label="Until (optional)"
                description="Leave empty: until the diet is changed."
                value={untilDay}
                onChange={setUntilDay}
                minValue={today}
              />
              <TextField
                label="Until time"
                description="24-hour clock, like 14:00."
                value={untilTime}
                onChange={setUntilTime}
                isDisabled={untilDay === null}
                {...(tried && untilIncomplete
                  ? { errorMessage: 'Write the time, like 14:00.' }
                  : {})}
              />
            </div>
          </>
        ) : null}
        <TextArea
          label="Note for the kitchen (optional)"
          description="The kitchen reads this, for example Halal or no pork. Don't write diagnoses."
          value={note}
          onChange={setNote}
          maxLength={KITCHEN_NOTE_MAX_LENGTH}
        />
        {tried && problem !== null && !checked.ok && checked.code !== 'diet.fluid_invalid' ? (
          <Notice tone="error">{problem}</Notice>
        ) : null}
        {place.error === null ? null : (
          <Notice tone="error">
            {errorText(place.error, "The diet wasn't saved. Try again.")}
          </Notice>
        )}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Go back
        </Button>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={place.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            setTried(true);
            if (ready) place.mutate();
          }}
        >
          {current === null ? 'Order diet' : 'Change diet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
